import { isFiledThrough, messageInboxSourceVersionMs, type MessageInboxStateDoc } from './messageInboxStateCore.ts';

/**
 * Pick the message that should open after the current one is filed to Past.
 *
 * The list is already in the same order the inbox renders it. Prefer the row
 * immediately below the current one. If the current row is last, keep the
 * reading flow alive with the nearest remaining row above it. A stale current
 * id falls back to the first available row instead of leaving an old detail
 * card on screen.
 */
export function nextAvailableMessageId(
  orderedIds: readonly string[],
  currentId: string
): string | null {
  const currentIndex = orderedIds.indexOf(currentId);
  if (currentIndex < 0) {
    return orderedIds.find((id) => id !== currentId) ?? null;
  }
  return orderedIds[currentIndex + 1] ?? orderedIds[currentIndex - 1] ?? null;
}

/**
 * Whether expandedItemId changing AWAY from `prevId` should sink that row
 * into Past (Bobbie, live, 2026-08-19: "NO it is not moving the card to
 * past after I click on it and read it! it just stays there," "they just
 * get stuck and I can't remove them!"). Only a generic notif row (the
 * `notif-` id shape genericNotifItems mints) sinks on close - every other
 * kind's "done" is its own explicit action (ack, claim, mark done), never
 * a side effect of merely collapsing the row. False for a no-op re-render
 * where prevId already equals nextId, so this only ever fires on a REAL
 * transition, once per close, never a stray extra unpin.
 */
export function shouldSinkOnClose(prevId: string | null, nextId: string | null): boolean {
  return !!prevId && prevId !== nextId && prevId.startsWith('notif-');
}

type ViewerAnnouncementState = {
  authorUid: string;
  ackRequired?: boolean;
  acks?: Readonly<Record<string, unknown>>;
  dismissedBy?: Readonly<Record<string, unknown>>;
  createdAt?: unknown;
  lastReplyAt?: unknown;
  editedAt?: unknown;
};

export function announcementNeedsAck(
  announcement: ViewerAnnouncementState,
  viewerUid: string,
  participatesInAudience: boolean
): boolean {
  return announcement.ackRequired === true
    && announcement.authorUid !== viewerUid
    && participatesInAudience
    && !announcement.acks?.[viewerUid]
    && !announcement.dismissedBy?.[viewerUid];
}

/**
 * Personal filing works for authors and read-only viewers too. It never
 * fills an acknowledgement roster or hides an outstanding required action.
 * An author's legacy acknowledgement still does not automatically file it.
 */
export function announcementHandledByViewer(
  announcement: ViewerAnnouncementState,
  viewerUid: string,
  inboxState?: Pick<MessageInboxStateDoc, 'filedThroughMs'>,
  participatesInAudience = true
): boolean {
  if (announcementNeedsAck(announcement, viewerUid, participatesInAudience)) return false;
  return (announcement.authorUid !== viewerUid
      && (!!announcement.acks?.[viewerUid] || !!announcement.dismissedBy?.[viewerUid]))
    || isFiledThrough(inboxState, messageInboxSourceVersionMs('board', announcement, viewerUid));
}

/**
 * Keep the temporary just-acted pin for every existing inbox kind except a
 * board announcement or a completed task. A board acknowledgement or
 * dismissal is the viewer's explicit filing action, so that row belongs in
 * Past as soon as the saved per-viewer state reaches the live snapshot.
 *
 * 'task' joined this exclusion in the feed-unification Slice D rebuild
 * (docs/feed-unification-plan-2026-08-18.md, "close the to-do-vanish gap"):
 * completing a task from the inbox must SINK it to Past, the same "never
 * demanded twice" shape a board ack already gets, not pin it inside Needs
 * you for the rest of the visit the way a class-post or bulletin ack does.
 * The task row's own visibility during the visit is carried separately, by
 * Messages.tsx's justCompletedTaskIds widening the taskItems filter itself
 * (taskNeedsMe alone would drop it from the inbox the instant it completes,
 * which is the exact "vanished outright" bug this slice closes) - this
 * function only decides Main vs Past for a row that is already present.
 */
export function shouldKeepInboxItemInMain(
  kind: string,
  justActed: boolean,
  alreadyHandled: boolean
): boolean {
  return !alreadyHandled || (kind !== 'board' && kind !== 'task' && justActed);
}

type RestorableNotification = { id: string; dismissed?: boolean };

/**
 * Put a just-filed notification back into the inbox source list for the
 * rest of the visit. useMyNotifications strips dismissed docs before the
 * Messages page ever sees them (visibleNotificationWindow), so the moment
 * an opened card's files-itself-read write echoes back, the doc is ABSENT
 * from the hook's list and the open card unmounts under the reader's
 * finger (live staff report, 2026-08-19: "they flash but don't open").
 * The page stashes the doc at filing time and this restores it, appended,
 * only while the hook's list does not carry that id - the stash never
 * duplicates a doc that is still present, and it never resurrects one the
 * viewer filed on an earlier visit (the stash is visit-scoped state).
 */
export function restoreJustFiledNotifications<T extends RestorableNotification>(
  fromHook: readonly T[],
  justFiled: ReadonlyMap<string, T>
): T[] {
  if (justFiled.size === 0) return [...fromHook];
  const present = new Set(fromHook.map((n) => n.id));
  const restored: T[] = [...fromHook];
  for (const doc of justFiled.values()) {
    if (!present.has(doc.id)) restored.push(doc);
  }
  return restored;
}

/**
 * The full notification source list Messages.tsx's generic notif kind
 * builds InboxItems from - RULING UPDATE (Bobbie, live, 2026-08-19: "NO it
 * is not moving the card to past after I click on it and read it! it just
 * stays there", "they just get stuck and I can't remove them!"). Reading a
 * card and moving on must sink it into Past, not just keep it pinned in
 * Main for the visit or - worse - make it vanish outright. That means a
 * DISMISSED generic notif has to stay reachable by this page at all, which
 * it was not before this: `live` (useMyNotifications' own `notifications`)
 * never carries a dismissed doc by construction (visibleNotificationWindow
 * strips it before the hook returns), so the only way a dismissed one
 * could ever be seen here was the just-filed stash - and only for the rest
 * of THIS visit.
 *
 * `dismissedWindow` (useMyNotifications' new `dismissed` field, same
 * bounded query, zero extra reads) closes that gap: once a doc is in it,
 * Messages' existing Main/Past split (shouldKeepInboxItemInMain +
 * inboxItemAlreadyHandled's 'notif' case, which already reads
 * `dismissed === true`) routes it to Past on its own - the fix here is
 * only about SUPPLYING the doc, not about deciding where it lands.
 *
 * live+stash first (restoreJustFiledNotifications, unchanged - still the
 * thing that keeps an OPEN card from unmounting mid-read while the
 * dismiss write has not echoed yet), then any dismissedWindow doc not
 * already present, so the same id is never listed twice regardless of
 * which source currently carries it.
 */
export function combinedNotificationSourcesForInbox<T extends RestorableNotification>(
  live: readonly T[],
  dismissedWindow: readonly T[],
  justFiled: ReadonlyMap<string, T>
): T[] {
  const withStash = restoreJustFiledNotifications(live, justFiled);
  const present = new Set(withStash.map((n) => n.id));
  const combined: T[] = [...withStash];
  for (const doc of dismissedWindow) {
    if (!present.has(doc.id)) {
      combined.push(doc);
      present.add(doc.id);
    }
  }
  return combined;
}

/**
 * Which class post ids still actually render as a classPost row in the
 * inbox for THIS viewer, for Messages.tsx's orphan guard
 * (notificationAlreadyRenderedElsewhere) to check a notification's
 * classPostId against.
 *
 * BUG THIS CLOSES (bounce review, reachability rider): Messages.tsx's
 * allViewItems strips every item.kind === 'classPost' outright for a
 * tucked role (foh only today: tuckClassBoardsForRole; ensemble was dropped
 * from it when Jr Staff became one person on either sign-in), so that role
 * never gets a classPost row no matter what inboxClassPosts contains. The
 * old inline renderableClassPostIds was built from inboxClassPosts alone,
 * before the tuck, so for a tucked viewer it kept reporting a post as
 * "rendered" that had actually just been stripped. The orphan guard
 * trusted that false positive and suppressed the generic notif card too -
 * a class post addressed to a front desk account personally
 * produced NOTHING on their inbox: no classPost row (tucked away) and no
 * fallback card (wrongly believed already covered). Passing
 * tuckClassBoardsForRole through here and returning an empty set for a
 * tucked viewer is what lets the notification fall through to the generic
 * card instead of vanishing.
 */
export function renderableClassPostIdsForInbox(
  inboxClassPostIds: readonly string[],
  tuckClassBoardsForRole: boolean
): Set<string> {
  if (tuckClassBoardsForRole) return new Set();
  return new Set(inboxClassPostIds);
}

/**
 * The class post feed's activity clock, in epoch ms: the latest moment
 * worth resurfacing the post for. Takes already-guarded millisecond values
 * (Messages.tsx's own timestampMs, run over the real Firestore Timestamps)
 * rather than raw Timestamps, so this stays a plain function with nothing
 * to stub and no Timestamp import of its own.
 *
 * editedAt joined createdAt and lastReplyAt here (bounce review): before
 * this, correcting a class post's wording stamped editedAt and
 * lastChangedAt (netlify/functions/classPostRecord.mjs) but this clock
 * never read either, so a parent who had already filed the ORIGINAL
 * wording to Past never saw the correction - "Saturday call time 5pm"
 * filed away, corrected to 6pm, and the feed never moved. Folding editedAt
 * in reopens the post through the exact same mechanism a new reply
 * already uses: isFiledThrough (messageInboxStateCore.ts) compares the
 * viewer's filedThroughMs, captured before the edit, against this now-
 * later value, so an edited post un-files itself from Past automatically -
 * no separate "was this in Past" special case needed.
 */
export function classPostFeedActivityMs(
  createdAtMs: number,
  lastReplyAtMs: number,
  editedAtMs: number
): number {
  return Math.max(createdAtMs, lastReplyAtMs, editedAtMs);
}
